import type { CourseData, Namespace, SlideNamespaces } from '../course/model.js';
import { COURSE_DATA_KEY, courseDataSchema, emptyCourseData } from '../course/model.js';
import { migrate } from '../migrations/index.js';
import { checksum } from './checksum.js';
import type {
  SettingsBackend,
  SidecarBackend,
  StoreEvent,
  StoredEnvelope,
  Unsubscribe,
} from './types.js';
import { StoreError } from './types.js';

export interface CourseStoreOptions {
  readonly settings: SettingsBackend;
  readonly sidecar?: SidecarBackend;
  /** Write coalescing window. A `saveAsync` per keystroke is a performance complaint. */
  readonly debounceMs?: number;
  /** Fraction of capacity at which we warn and start looking at the sidecar. */
  readonly quotaWarningRatio?: number;
  readonly now?: () => Date;
  readonly writerId?: string;
  readonly courseId?: string;
  readonly key?: string;
}

/**
 * Contract 1 of docs/plans/41-cross-cutting-contracts.md: the single owner of
 * course data inside the .pptx. Nothing else in the repo touches
 * `Office.context.document.settings`.
 */
export class CourseStore {
  readonly #settings: SettingsBackend;
  readonly #sidecar: SidecarBackend | undefined;
  readonly #debounceMs: number;
  readonly #quotaWarningRatio: number;
  readonly #now: () => Date;
  readonly #writerId: string;
  readonly #key: string;

  #data: CourseData | undefined;
  #baseRevision = 0;
  #listeners = new Set<(e: StoreEvent) => void>();
  #pendingTimer: ReturnType<typeof setTimeout> | undefined;
  #pendingSave: Promise<void> | undefined;
  #writeChain: Promise<void> = Promise.resolve();

  constructor(options: CourseStoreOptions) {
    this.#settings = options.settings;
    this.#sidecar = options.sidecar;
    this.#debounceMs = options.debounceMs ?? 800;
    this.#quotaWarningRatio = options.quotaWarningRatio ?? 0.8;
    this.#now = options.now ?? (() => new Date());
    this.#writerId = options.writerId ?? randomId();
    this.#key = options.key ?? COURSE_DATA_KEY;
    this.#defaultCourseId = options.courseId ?? 'course';
  }

  readonly #defaultCourseId: string;

  subscribe(listener: (e: StoreEvent) => void): Unsubscribe {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  /**
   * Reads, migrates and caches the stored course data. A corrupt or
   * from-the-future blob raises rather than being silently replaced — losing a
   * lecturer's quiz quietly is the failure this whole module exists to prevent.
   */
  async getCourse(): Promise<CourseData> {
    if (this.#data) return this.#data;

    const raw = this.#settings.read(this.#key);
    if (raw === undefined || raw === null) {
      this.#data = emptyCourseData(this.#defaultCourseId);
      this.#baseRevision = 0;
      return this.#data;
    }

    const envelope = parseEnvelope(raw);
    if (!envelope) {
      throw new StoreError(
        'corrupt-blob',
        'The SlideForge data in this deck could not be read. It has been left untouched; save a copy of the file before editing.',
      );
    }

    let payload: string | undefined = envelope.payload;
    if (envelope.location === 'sidecar') {
      if (!this.#sidecar?.available) {
        throw new StoreError(
          'sidecar-unavailable',
          'This course is too large to fit inside the deck, and the SlideForge service is not running. Start the service to edit it; the deck has not been changed.',
        );
      }
      payload = await this.#sidecar.get(envelope.pointer ?? '');
    }

    if (payload === undefined) {
      throw new StoreError('corrupt-blob', 'The stored course data is missing its payload.');
    }
    if (checksum(payload) !== envelope.checksum) {
      throw new StoreError(
        'corrupt-blob',
        'The stored course data failed its integrity check and was not loaded. The deck has not been changed.',
      );
    }

    const result = migrate(JSON.parse(payload));
    if (!result.ok) {
      const code = result.failure.reason === 'from-the-future' ? 'from-the-future' : 'migration-failed';
      throw new StoreError(code, result.failure.message);
    }

    this.#data = result.data;
    this.#baseRevision = envelope.revision;
    return this.#data;
  }

  /** Read-modify-write, serialised so two callers cannot interleave. */
  async updateCourse(mutator: (draft: CourseData) => void | CourseData): Promise<CourseData> {
    const run = this.#writeChain.then(async () => {
      const current = await this.getCourse();
      const draft = structuredCloneCompat(current);
      const returned = mutator(draft);
      const next = courseDataSchema.parse(returned ?? draft);
      this.#data = { ...next, updatedAt: this.#now().toISOString() };
      this.#emit({ kind: 'changed', data: this.#data });
      this.#scheduleSave();
      return this.#data;
    });
    this.#writeChain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  getSlideData<T>(slideId: string, namespace: Namespace): T | undefined {
    const slide = this.#data?.slides[slideId];
    return slide ? (slide[namespace] as T | undefined) : undefined;
  }

  async setSlideData<T>(slideId: string, namespace: Namespace, value: T): Promise<void> {
    await this.updateCourse((draft) => {
      const existing: SlideNamespaces = draft.slides[slideId] ?? {};
      draft.slides[slideId] = { ...existing, [namespace]: value };
    });
  }

  async clearSlideData(slideId: string, namespace: Namespace): Promise<void> {
    await this.updateCourse((draft) => {
      const existing = draft.slides[slideId];
      if (!existing) return;
      const { [namespace]: _removed, ...rest } = existing;
      if (Object.keys(rest).length === 0) delete draft.slides[slideId];
      else draft.slides[slideId] = rest;
    });
  }

  /** Forces the pending write out now. Call before the document closes. */
  async flush(): Promise<void> {
    if (this.#pendingTimer !== undefined) {
      clearTimeout(this.#pendingTimer);
      this.#pendingTimer = undefined;
    }
    await this.#writeChain;
    await this.#write();
  }

  #scheduleSave(): void {
    if (this.#pendingTimer !== undefined) clearTimeout(this.#pendingTimer);
    this.#pendingTimer = setTimeout(() => {
      this.#pendingTimer = undefined;
      this.#pendingSave = this.#write().catch((error: unknown) => {
        this.#emit({
          kind: 'error',
          code: error instanceof StoreError ? error.code : 'save-failed',
          message: error instanceof Error ? error.message : String(error),
        });
      });
    }, this.#debounceMs);
  }

  async #write(): Promise<void> {
    if (this.#pendingSave) {
      const inFlight = this.#pendingSave;
      this.#pendingSave = undefined;
      await inFlight;
    }
    const data = this.#data;
    if (!data) return;

    this.#detectClobber();

    const payload = JSON.stringify(data);
    const sum = checksum(payload);
    const bytes = byteLength(payload);
    const capacity = this.#settings.capacityBytes;

    if (bytes > capacity * this.#quotaWarningRatio) {
      this.#emit({
        kind: 'quota-warning',
        bytes,
        capacityBytes: capacity,
        message: `Course data is ${formatBytes(bytes)} of an estimated ${formatBytes(capacity)} that fits inside the deck.`,
      });
    }

    const envelopeBase = {
      envelopeVersion: 1,
      revision: this.#baseRevision + 1,
      writer: this.#writerId,
      writtenAt: this.#now().toISOString(),
      checksum: sum,
    } as const;

    if (bytes <= capacity) {
      const envelope: StoredEnvelope = { ...envelopeBase, location: 'settings', payload };
      this.#settings.write(this.#key, envelope);
      await this.#save();
      this.#baseRevision = envelope.revision;
      this.#emit({ kind: 'saved', bytes, location: 'settings' });
      return;
    }

    // Quota handling is a first-class path, not an error branch.
    if (!this.#sidecar?.available) {
      throw new StoreError(
        'quota-exceeded',
        `This course no longer fits inside the PowerPoint file (${formatBytes(bytes)} of about ${formatBytes(capacity)}). Start the SlideForge service so the extra data can be stored alongside the deck, or remove some media from your quizzes. Nothing has been lost — your last saved version is intact.`,
      );
    }

    const pointer = await this.#sidecar.put(data.courseId, payload);
    const envelope: StoredEnvelope = { ...envelopeBase, location: 'sidecar', pointer };
    this.#settings.write(this.#key, envelope);
    await this.#save();
    this.#baseRevision = envelope.revision;
    this.#emit({ kind: 'saved', bytes, location: 'sidecar' });
  }

  async #save(): Promise<void> {
    try {
      await this.#settings.save();
    } catch (error) {
      throw new StoreError(
        'save-failed',
        error instanceof Error ? error.message : 'The deck could not be saved.',
      );
    }
  }

  /**
   * OneDrive co-authoring means someone else's PowerPoint may have written this
   * deck since we read it. Policy: last-write-wins on the whole blob, but never
   * silently — a clobber is always reported.
   */
  #detectClobber(): void {
    const raw = this.#settings.read(this.#key);
    const current = parseEnvelope(raw);
    if (!current) return;
    if (current.revision > this.#baseRevision && current.writer !== this.#writerId) {
      this.#emit({
        kind: 'clobber-detected',
        ourWriter: this.#writerId,
        theirWriter: current.writer,
        message:
          'Someone else edited this deck with SlideForge while it was open here. Your version is about to overwrite theirs.',
      });
      this.#baseRevision = current.revision;
    }
  }

  #emit(event: StoreEvent): void {
    for (const listener of this.#listeners) {
      try {
        listener(event);
      } catch {
        // A misbehaving listener must not take the save path down with it.
      }
    }
  }
}

function parseEnvelope(raw: unknown): StoredEnvelope | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const o = raw as Record<string, unknown>;
  if (o['envelopeVersion'] !== 1) return undefined;
  if (typeof o['checksum'] !== 'string') return undefined;
  if (typeof o['revision'] !== 'number') return undefined;
  if (o['location'] !== 'settings' && o['location'] !== 'sidecar') return undefined;
  return {
    envelopeVersion: 1,
    revision: o['revision'],
    writer: typeof o['writer'] === 'string' ? o['writer'] : 'unknown',
    writtenAt: typeof o['writtenAt'] === 'string' ? o['writtenAt'] : '',
    checksum: o['checksum'],
    location: o['location'],
    payload: typeof o['payload'] === 'string' ? o['payload'] : undefined,
    pointer: typeof o['pointer'] === 'string' ? o['pointer'] : undefined,
  };
}

function byteLength(s: string): number {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(s).length;
  return s.length;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} bytes`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function structuredCloneCompat<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function randomId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}
